import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import {
  clearEntities as clearRegionsAction,
  getCountries as getCountriesByRegion,
  getRegions as getRegionsAction,
} from '../../redux/actions/regionsActions';
import {
  clearEntities as clearRestaurantsAction,
  getEntities as getRestaurantsAction,
  setPageAction,
  setRowsPerPageAction,
} from '../../redux/actions/restaurantsActions';
import TableView from '../TableView/TableView';
import {
  selectCountries,
  selectCountriesById,
  selectRegions,
  selectRegionsById,
} from '../../redux/selectors/regionsSelector';
import { isValidNumber } from '../../utils/utils';

const useStyles = makeStyles(() => ({
  paperStyle: {
    padding: '6px',
  },
  textFieldStyles: {
    width: '100%',
  },
  searchElement: {
    paddingRight: '10px',
  },
  marginStyle: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  textAlignRight: {
    textAlign: 'right',
  },
  textAlignCenter: {
    textAlign: 'center',
  },
}));

/**
 *
 * @param regionsIds
 * @param regions
 * @param getRegions
 * @param getRestaurants
 * @param columns
 * @param restaurants
 * @param loaded
 * @param clearRestaurants
 * @param countriesIds
 * @param countries
 * @param paginationConfig
 * @param setRowsPerPage
 * @param setPage
 * @param getCountriesByRegion
 * @param selectedBrand
 * @param clearRegions
 * @constructor
 */
function AdvancedSearch({
  regionsIds,
  regions,
  getRegions,
  getRestaurants,
  columns,
  restaurants,
  loaded,
  clearRestaurants,
  countriesIds,
  countries,
  paginationConfig,
  setRowsPerPage,
  setPage,
  getCountriesByRegion,
  selectedBrand,
  clearRegions,
}) {
  const classes = useStyles();
  const [searchObject, setSearchObject] = useState<any>({
    ownership: 'Company',
    region: '',
    country: '',
    city: '',
    id: '',
  });
  const [idError, setIdError] = useState<string>('');
  const [restaurantsTreated, setRestaurantsTreated] = useState([]);

  useEffect(() => {
    getRegions();
    clearRestaurants();
    clearRegions();
  }, [getRegions, clearRestaurants, clearRegions]);

  useEffect(() => {
    clearRestaurants();
    clearRegions();
  }, [clearRegions, clearRestaurants, selectedBrand]);

  useEffect(() => {
    if (restaurants) {
      setRestaurantsTreated(
        restaurants.map(restaurant => {
          const newRestaurant = { ...restaurant };
          newRestaurant.action = (
            <IconButton
              component={Link}
              to={`/restaurants/${newRestaurant.id}`}
            >
              <VisibilityIcon />
            </IconButton>
          );
          return newRestaurant;
        }),
      );
    }
  }, [restaurants]);

  const onInputTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSearchObject({ ...searchObject, [name]: value });
    if (name === 'id') {
      if (!isValidNumber(value)) {
        setIdError('Invalid Id');
      } else {
        setIdError('');
      }
    }
  };

  const onInputSelectChange = event => {
    setSearchObject({
      ...searchObject,
      [event.target.name]: event.target.value,
    });
    if (event.target.name === 'region') {
      getCountriesByRegion(event.target.value);
    }
  };

  const onClickSearch = () => {
    getRestaurants({
      ...searchObject,
      page: 1,
      limit: paginationConfig.rowsPerPage,
      brand: selectedBrand,
    });
    setPage(1);
  };

  const onClickClear = () => {
    setSearchObject({
      ownership: 'Company',
      region: '',
      country: '',
      city: '',
      id: '',
    });
    clearRestaurants();
    clearRegions();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
    getRestaurants({
      ...searchObject,
      page: 1,
      limit: +event.target.value,
      brand: selectedBrand,
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
    getRestaurants({
      ...searchObject,
      page: newPage + 1,
      limit: paginationConfig.rowsPerPage,
      brand: selectedBrand,
    });
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (!idError.length) {
        onClickSearch();
      }
    }
  };
  return (
    <div>
      <Typography variant="h5" style={{ marginBottom: 15 }}>
        Advanced Search
      </Typography>
      <Paper elevation={2} className={classes.paperStyle}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={3} className={classes.searchElement}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Ownership:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Select
                  value={searchObject['ownership']}
                  label="Ownership"
                  inputProps={{
                    name: 'ownership',
                    id: 'search-type',
                    size: 'small',
                  }}
                  onChange={onInputSelectChange}
                >
                  <MenuItem key="owner_Corporate" value="Company">
                    Corporate
                  </MenuItem>
                  <MenuItem key="owner_Franchise" value="Franchise">
                    Franchise
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3} className={classes.searchElement}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Region:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Select
                  label="Region"
                  value={searchObject['region']}
                  inputProps={{
                    name: 'region',
                    id: 'search-type',
                    size: 'small',
                  }}
                  onChange={onInputSelectChange}
                >
                  <MenuItem key="region_empty" value="" />
                  {regionsIds.map(id => (
                    <MenuItem key={`region_${id}`} value={regions[id].id}>
                      {regions[id].name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3} className={classes.searchElement}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Country:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Select
                  label="Country"
                  value={searchObject['country']}
                  inputProps={{
                    name: 'country',
                    id: 'search-type',
                    size: 'small',
                  }}
                  onChange={onInputSelectChange}
                >
                  <MenuItem key="country_empty" value="" />
                  {countriesIds.map(id => (
                    <MenuItem key={`country_${id}`} value={countries[id].id}>
                      {countries[id].description}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3} className={classes.searchElement}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>City:</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  onChange={onInputTextChange}
                  className={classes.textFieldStyles}
                  size="small"
                  variant="outlined"
                  value={searchObject['city']}
                  name="city"
                  onKeyPress={onKeyPress}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3} className={classes.searchElement}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>ID:</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  onChange={onInputTextChange}
                  className={classes.textFieldStyles}
                  size="small"
                  variant="outlined"
                  value={searchObject['id']}
                  name="id"
                  error={!!idError.length}
                  helperText={idError}
                  onKeyPress={onKeyPress}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          className={`${classes.marginStyle} ${classes.textAlignRight}`}
          container
          direction="row"
          justify="flex-end"
        >
          <Grid item xs={4} sm={2}>
            <Button
              onClick={onClickSearch}
              variant="contained"
              color="secondary"
              disabled={!!idError.length}
            >
              <Typography>Search</Typography>
            </Button>
          </Grid>
          <Grid item xs={4} sm={2}>
            <Button onClick={onClickClear} variant="contained" color="primary">
              <Typography>Clear</Typography>
            </Button>
          </Grid>
        </Grid>
        {loaded &&
          (restaurants.length ? (
            <TableView
              columns={columns}
              rows={restaurantsTreated}
              paginationConfig={paginationConfig}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangePage}
            />
          ) : (
            <Typography
              className={`${classes.marginStyle} ${classes.textAlignCenter}`}
            >
              There is no coincidences, please try with other filters
            </Typography>
          ))}
      </Paper>
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  regionsIds: selectRegionsById(state),
  regions: selectRegions(state),
  countriesIds: selectCountriesById(state),
  countries: selectCountries(state),
  columns: state.RestaurantsReducer.columns,
  restaurants: state.RestaurantsReducer.entities,
  loading: state.RestaurantsReducer.loading,
  loaded: state.RestaurantsReducer.loaded,
  paginationConfig: state.RestaurantsReducer.paginationConfig,
  selectedBrand: state.UIReducer.selectedBrand,
});

const mapDispatchToProps = {
  getRegions: getRegionsAction,
  getRestaurants: getRestaurantsAction,
  clearRestaurants: clearRestaurantsAction,
  setRowsPerPage: setRowsPerPageAction,
  setPage: setPageAction,
  getCountriesByRegion: getCountriesByRegion,
  clearRegions: clearRegionsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdvancedSearch);
